import {
    ChangeDetectorRef,
    Component,
    Inject,
    Input,
    OnInit,
} from '@angular/core';
import { Validators } from '@angular/forms';
import { takeUntil } from 'rxjs/operators';

import { NDDFormErrorValidatorService } from '../ndd-ng-form-error/shared/ndd-ng-form-error-validator.service';
import { NDDFormErrorService } from '../ndd-ng-form-error/shared/ndd-ng-form-error.service';
import { NDDFormsBaseComponent } from '../shared/ndd-ng-forms.base-component';

/** Componente textarea (representa um campo de texto multilinha). */
@Component({
    selector: 'ndd-ng-form-textarea',
    template: require('./ndd-ng-form-textarea.component.html'),
})
export class NDDFormTextAreaComponent extends NDDFormsBaseComponent implements OnInit {
    private static LINE_BREAK_REGEX: RegExp = /\r?\n|\r/g;
    private static MULTI_SPACES_REGEX: RegExp = /\s+/g;

    /** Número de linhas de texto visíveis (altura do campo). */
    @Input() public controlRows: number = 5;
    /** Define o controle como redimensionável. */
    @Input() public controlResizable: boolean = false;
    /** Define verificação de ortografia do texto inserido no controle. */
    @Input() public controlSpellcheck: boolean = false;
    /** Define permissão de uso de quebras de linha. */
    @Input() public controlAllowLineBreak: boolean = true;

    /** Define se o valor do controle pode ser completado automaticamente pelo navegador. */
    @Input() public controlAutocomplete?: 'off' | 'on';
    /** Define tamanho máximo de caracteres. */
    @Input() public controlMaxLength?: number;
    /** Define tamanho mínimo de caracteres. */
    @Input() public controlMinLength?: number;
    /** Define placeholder para exibição no controle quando valor for vazio. */
    @Input() public controlPlaceholder?: string;
    /** Define controle como somente leitura. */
    @Input() public controlReadonly?: boolean;
    /** Remover espaços antes e depois do valor. */
    @Input() public controlTrim?: boolean;

    constructor(@Inject(Window) private window: Window, formErrorService: NDDFormErrorService, cdr: ChangeDetectorRef) {
        super(formErrorService, cdr);
    }

    public ngOnInit(): void {
        super.addControl();

        if (this.controlRequired) {
            super.cacheValidator(NDDFormErrorValidatorService.required(this.controlTrim));
        }

        if (this.controlMaxLength || this.controlMaxLength === 0) {
            super.cacheValidator(Validators.maxLength(this.controlMaxLength));
        }

        if (this.controlMinLength || this.controlMinLength === 0) {
            super.cacheValidator(Validators.minLength(this.controlMinLength));
        }

        super.addValidators();

        this.control.valueChanges.pipe(
            takeUntil(this.ngUnsubscribe))
            .subscribe((value: string) => {
                if (value && this.controlTrim) {
                    value = value.trim();
                }

                if (!this.controlAllowLineBreak) {
                    value = this.removeLineBreak(value);
                }

                this.controlValueChange.emit(value);
            });
    }

    /**
     * Valida permissão de uso de quebra de linha.
     *
     * @memberof NDDFormTextAreaComponent
     */
    public allowLineBreak(): boolean {
        return this.controlAllowLineBreak;
    }

    /**
     * Executa `String.trim()` no valor do input.
     *
     * @memberof NDDFormTextAreaComponent
     */
    public trimmer(): void {
        if (this.control.value && this.controlTrim) {
            this.control.setValue(this.control.value.trim());
        }
    }

    /**
     * Ajusta valor do controle após eventos `drop` e `paste`.
     *
     * @memberof NDDFormTextAreaComponent
     */
    public fixDroppedValue(): void {
        if (this.controlAllowLineBreak) { return; }

        this.window.setTimeout(() => {
            const newValue: string = this.removeLineBreak(this.control.value);
            this.control.setValue(newValue);
        });
    }

    private removeLineBreak(value: string): string {
        if (!value) { return ''; }

        return value
            .replace(NDDFormTextAreaComponent.LINE_BREAK_REGEX, ' ')
            .replace(NDDFormTextAreaComponent.MULTI_SPACES_REGEX, ' ');
    }
}
